/**
 * Copyright (C) 2018 Robert Bosch Manufacturing Solutions GmbH, Germany. All rights reserved.
 */

import {
  Location,
  LocationStrategy,
  PathLocationStrategy
} from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, Event as RouterEvent } from '@angular/router';
import {
  BciImprintComponent,
  BciLoaderService,
  BciSidebarService,
  LogoutComponent,
  ModalWindowService,
  NavigationService,
  SidebarNavItem
} from '@bci-web-core/core';
import { TranslateService } from '@ngx-translate/core';
import {
  AuthConfig,
  JwksValidationHandler,
  OAuthService
} from 'angular-oauth2-oidc';
import { Observable, Subject, of } from 'rxjs';
import { SystemPrivacyModalComponent } from 'src/app/Components/user/system-privacy-modal/system-privacy-modal.component';
import { ButtonConfig } from 'src/app/models/button.component.model';
import { AuthService } from 'src/app/services/auth.service';
import { DataService } from 'src/app/services/data.service';
import { CommonService, SiteService } from 'src/app/services/index';
import { AlertService, AuthGuard, WarningDialog } from 'src/app/shared/index';
import { environment } from 'src/environments/environment';
import { ContactUsModalComponent } from './Components/user/contact-us-modal/contact-us-modal.component';
import { AuthUserInfo } from './models/user-management.model';
import { AppConfigService } from './services/app-config.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'bci-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [
    Location,
    { provide: LocationStrategy, useClass: PathLocationStrategy }
  ]
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  title = 'Energy Management';
  sidebarLinks: SidebarNavItem[] = [];
  public sidebarFooterLinks: SidebarNavItem[];
  extendedNavigation = false;
  location: Location;
  userActivity;
  userInactive: Subject<any> = new Subject();
  INACTIVE_CHECK_NUMBER = 6;
  inactiveCount = 0;
  // Get from environment.ts
  MAX_ALLOWED_INACTIVE_MINUTES = 30;
  isDialogOpened = false;
  authConfig: AuthConfig = {};
  lang: string;
  langData: any;
  langFooter: any;
  footerTittle: any;
  loadingBar: any;
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private titleService: Title,
    private navigationService: NavigationService,
    private modalWindowService: ModalWindowService,
    private sidebarService: BciSidebarService,
    public auth: AuthGuard,
    private authService: AuthService,
    private commonService: CommonService,
    private elRef: ElementRef,
    private dialog: MatDialog,
    private legacyDialog: MatLegacyDialog,
    location: Location,
    private siteService: SiteService,
    private dataService: DataService,
    private oauthService: OAuthService,
    public appConfig: AppConfigService,
    private translate: TranslateService,
    private alertService: AlertService,
    private loader: BciLoaderService
  ) {
    this.router.events.subscribe((e: RouterEvent) => {
      this.navigationInterceptor(e);
    });
    this.loadingBar = this.loader.showProgressBar();
    this.location = location;
    const userIdleTimeInMinutes = `${environment.userIdleTimeInMinutes}`;
    this.MAX_ALLOWED_INACTIVE_MINUTES =
      Number(userIdleTimeInMinutes) * 1000 * 60;
    this.inactiveCount = 0;

    this.translate.stream('dialogBox').subscribe((subStream: any) => {
      this.langData = subStream;
    });
    this.translate.stream('sidebar.subStream.footer').subscribe((sub: any) => {
      this.langFooter = sub;
      this.sidebarFooterLinks = [
        {
          title: this.langFooter.logout,
          overlay: {
            component: LogoutComponent,
            config: {
              title: this.langFooter.confirmLogout,
              onLogout: () => this.logout()
            }
          },
          icon: 'Bosch-Ic-user'
        },
        {
          id: 'imprint',
          title: this.langFooter.imprint,
          icon: 'Bosch-Ic-information',
          items: [
            {
              id: 'usagePolicy',
              title: this.langFooter.usagePolicy,
              cb: () => this.onPolicy()
            },
            {
              id: 'Copyright',
              title: this.langFooter.copyright,
              cb: () => this.onAbout()
            }
          ]
        },
        {
          title: this.langFooter.contactUs,
          cb: () => this.onContact(),
          icon: 'bosch-ic-call'
        }
      ];
    });

    this.startCounter();

    this.userInactive.subscribe(() => {
      // Ignore check if standing at login page
      if (this.router.url !== '/') {
        // After idle time
        let inactiveTime =
          new Date().getTime() -
          Number(localStorage.getItem('lastActivityTime'));
        // Every 1 min, if there is any activity, send heartbeat to server to keep alive
        // This is to avoid difference time count in case user opens multiple tabs
        if (this.inactiveCount === this.INACTIVE_CHECK_NUMBER - 1) {
          // Send heart beat to reset counter of Back End.
          // this.authService.sendHeartBeat().subscribe();
        }
        this.inactiveCount++;

        if (
          inactiveTime >= this.MAX_ALLOWED_INACTIVE_MINUTES ||
          localStorage.getItem('needForceLogout') === 'true'
        ) {
          // Alert and force logout
          localStorage.setItem('needForceLogout', 'true');
          this.showDialogAndLogout();
        } else {
          this.startCounter();
        }
      } else {
        // Continue time counter
        this.startCounter();
      }
    });

    // this.authConfig = {
    //   // Url of the Identity Provider
    //   issuer: this.appConfig.config.issuer,
    //   // URL of the SPA to redirect the user to after login
    //   redirectUri: this.appConfig.config.redirectUri,
    //   // The SPA's id. The SPA is registered with this id at the auth-server
    //   clientId: this.appConfig.config.clientId,
    //   // TODO: updating to CodeFlow does not work if responseType is enabled
    //   // responseType: this.appConfig.config.responseType,
    //   // set the scope for the permissions the client should request
    //   scope: this.appConfig.config.scope,
    //   strictDiscoveryDocumentValidation: false,
    //   requestAccessToken: true,
    //   disableAtHashCheck: true,
    //   skipIssuerCheck: true,
    //   dummyClientSecret: this.appConfig.config.clientSecret,
    //   showDebugInformation: true
    // };

    translate.setDefaultLang('en');
    translate.use(localStorage.getItem('lang') || 'en');
    this.loader.hideProgressBar(this.loadingBar);
  }
  navigationInterceptor(event: RouterEvent): void {
    if (event instanceof NavigationStart) {
      
    }
    if (event instanceof NavigationEnd) {

    }

    // Set loading state to false in both of the below events to hide the spinner in case a request fails
    if (event instanceof NavigationCancel) {

    }
    if (event instanceof NavigationError) {

    }
  }

  private configure() {
    this.oauthService.configure(this.authConfig);
    this.oauthService.setupAutomaticSilentRefresh();
    this.oauthService.setStorage(localStorage);
    this.oauthService.tokenValidationHandler = new JwksValidationHandler();
    this.oauthService.loadDiscoveryDocumentAndLogin().then(() => {
      if (!this.oauthService.hasValidAccessToken()) {
        this.oauthService.initCodeFlow();
      }
    });
  }

  public redirectToDaimler(status) {
    if (status === 'login') {
      this.authService.logout();
      this.oauthService.logOut();
      window.open(this.appConfig.config.redirectUri, '_self');
    } else {
      this.logout();
    }
  }

  public loginFromToken(tokenExternal) {
    //Validate the token
    //Store token and login
  }

  showDialogAndLogout() {
    if (this.dialog.openDialogs.length === 0) {
      this.isDialogOpened = true;
      const dialogRef = this.dialog.open(WarningDialog, { width: '50%' });
      dialogRef.componentInstance.confirmMessage = this.langData.sessionExpire;
      dialogRef.componentInstance.buttonConfig = [
        new ButtonConfig('Close', false, true, () => dialogRef.close(false))
      ];
      dialogRef.afterClosed().subscribe(() => {
        this.isDialogOpened = false;
        // In case of multiple tabs -> only call logout on first time
        if (localStorage.getItem('needForceLogout') === 'true') {
          this.logout();
        }
        this.sidebarService.setSidebarVisibility(false);
        this.router.navigate(['/']);
        localStorage.setItem('needForceLogout', 'false');
      });
    }
  }

  // Check on each 10s, if there is any activity within 1 min,
  // send heart beat to server to reset counter of server.
  startCounter() {
    this.userActivity = setTimeout(() => {
      this.userInactive.next(undefined);
    }, 60000 / this.INACTIVE_CHECK_NUMBER);
  }

  stopCounter() {
    clearTimeout(this.userActivity);
  }

  hasActivity() {
    localStorage.setItem('lastActivityTime', new Date().getTime().toString());
    this.inactiveCount = 0;

    this.resetCounter();
  }

  resetCounter() {
    this.stopCounter();
    this.startCounter();
  }

  @HostListener('window:mousemove') refreshUserState() {
    this.hasActivity();
  }
  @HostListener('document:keypress') handleKeyboardEvent() {
    this.hasActivity();
  }

  callBack() {}

  ngOnInit() {
    if (
      this.location.path() !== '/lang-change' &&
      !this.location.path().startsWith('/site')
    )
      if (this.commonService.isIE()) {
        const dialogRef = this.dialog.open(WarningDialog, { width: '50%' });
        dialogRef.componentInstance.confirmMessage = this.langData.IEsupport;

        dialogRef.afterClosed().subscribe((result) => {
          if (!result) {
            window.history.go(-1);
          }
        });
        return;
      }
    this.lang = localStorage.getItem('lang') || 'en';
    localStorage.setItem('lang', this.lang);
    this.titleService.setTitle(this.title);
    const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    // if (currentUser === null || currentUser === undefined) {
    //   this.redirectToDaimler('login');
    // }
    this.getSidebarLinks(currentUser, this.lang).subscribe(
      (sidebarLinks) => (this.auth.sidebarLinks = sidebarLinks)
    );

    if (this.detectNewWorkingSession()) {
      this.router.navigate(['/']);
    }
  }
  ngAfterViewInit() {
    let ele = this.elRef.nativeElement.querySelector('bci-burger');
    if (ele) {
      ele.addEventListener('click', this.onClick.bind(this));
    }
    this.titleService.setTitle(this.title);
    const headerEl =
      this.elRef.nativeElement.querySelector('bci-webcore-header');
    if (headerEl) {
      headerEl.setAttribute('style', 'display: none');
    }
  }

  get authenticated(): boolean {
    if (this.oauthService.hasValidAccessToken()) {
      return true;
    }
  }

  onClick() {
    this.sidebarService.getSidebarState().subscribe(() => {
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 500);
    });
  }
  getSidebarLinks(currentUser, lang): Observable<SidebarNavItem[]> {
    // const currentUser = JSON.parse(localStorage.getItem('currentUser'));
    if (this.commonService.isNullOrUndefined(currentUser)) {
      return this.navigationService.getNavigationItems();
    } else {
      this.authService.userActivities = currentUser.activities;
      this.authService.userRole = currentUser.role.name;

      this.authService.userMenus = currentUser.menuItems;
      const menu = [];
      const sidebarLinksUser: SidebarNavItem[] = this.authService.buildMenus(
        this.authService.defaultMenus,
        menu,
        true,
        lang
      );

      this.footerTittle =
        currentUser.fullName !== ''
          ? currentUser.fullName
          : currentUser.userName;
      return of(sidebarLinksUser);
    }
  }

  logout() {
    localStorage.removeItem('id_token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('currentUser');
    sessionStorage.removeItem('access_token');
    this.sidebarService.setSidebarVisibility(false);
    window.open(environment.logoutUrl_SSO, '_self');
  }

  onAbout() {
    const dialogRef = this.legacyDialog.open(BciImprintComponent, {
      width: '800px'
    });
    dialogRef.afterClosed().subscribe((result) => {});
  }
  onPolicy() {
    this.dataService
      .getData('./assets/text/privacy.txt', true, true)
      .subscribe((data) => {
        if (data) {
          // Incase can read -> Show policy popup
          const dialogRef = this.dialog.open(SystemPrivacyModalComponent, {
            width: '50%',
            maxHeight: '660px'
          });
          dialogRef.componentInstance.firstLogin = true;
          dialogRef.componentInstance.privacyMessage = data;
          dialogRef.afterClosed().subscribe((result) => {});
        }
      });
  }
  onContact() {
    this.dataService
      .getData('./assets/text/contact.txt', true, true)
      .subscribe((data) => {
        if (data) {
          const dialogRef = this.dialog.open(ContactUsModalComponent, {
            width: '30%',
            maxHeight: '300px'
          });
          dialogRef.componentInstance.privacyMessage = data;
          dialogRef.afterClosed().subscribe((result) => {});
        }
      });
  }
  ngOnDestroy() {}
  detectNewWorkingSession() {
    const loc: any = this.location;
    try {
      if (
        this.commonService.isNullOrUndefined(
          loc._platformStrategy._platformLocation._history.state
        )
      ) {
        return true;
      }
    } catch (err) {
      return false;
    }
  }

  redirectToSiteMasterPage(token) {
    this.authService.getUserDetail().subscribe((userInfo) => {
      if (userInfo !== null) {
        let fullName = '';

        const firstName = !this.commonService.isNullOrUndefined(
          userInfo.data.firstName
        )
          ? userInfo.data.firstName
          : '';
        const lastName = !this.commonService.isNullOrUndefined(
          userInfo.data.lastName
        )
          ? userInfo.data.lastName
          : '';
        if (firstName !== '' && lastName !== '') {
          fullName = firstName + ' ' + lastName;
        }

        const authUser = new AuthUserInfo(
          token,
          '',
          '',
          userInfo.data.username,
          fullName,
          userInfo.data.role,
          userInfo.data.flatMenus,
          0,
          userInfo.data.useSSO,
          userInfo.data.alreadyLoggedIn,
          userInfo.data.activities
        );
        localStorage.setItem('currentUser', JSON.stringify(authUser));
        this.authService.userActivities = userInfo.data.activities;
        this.authService.userRole = userInfo.data.role.name;
        this.authService.userMenus = userInfo.data.flatMenus;
        const menu = [];
        const sidebarLinksUser: SidebarNavItem[] = this.authService.buildMenus(
          this.authService.defaultMenus,
          menu,
          true
        );

        this.auth.sidebarLinks = sidebarLinksUser;
        this.sidebarFooterLinks[0].title =
          authUser.fullName !== '' ? authUser.fullName : authUser.userName;
        console.log('authUser.alreadyLoggedIn --> ' + authUser.alreadyLoggedIn);
        if (!authUser.alreadyLoggedIn) {
          if (this.dialog.openDialogs.length === 0) {
            this.dataService
              .getData('./assets/text/privacy.txt', true, true)
              .subscribe((data) => {
                if (data) {
                  // Incase can read -> Show policy popup
                  const dialogRef = this.dialog.open(
                    SystemPrivacyModalComponent,
                    {
                      width: '50%',
                      maxHeight: '660px'
                    }
                  );
                  dialogRef.componentInstance.privacyMessage = data;
                  dialogRef.afterClosed().subscribe((dialogData) => {
                    if (dialogData && dialogData.data) {
                      this.authService.takeOutFirstTimeOfUser().subscribe();
                      this.navigate();
                    }
                  });
                } else {
                  // Incase cannot read privacy file -> Navigate
                  this.navigate();
                }
              });
          }
        } else {
          // In case: not the first time user login
          this.navigate();
        }
      }
    });
  }

  navigate() {
    // Store time for checking inactive user
    localStorage.setItem('lastActivityTime', new Date().getTime().toString());
    localStorage.setItem('needForceLogout', 'false');
    //
    this.siteService.getSiteDetails().subscribe((sites) => {
      if (sites) {
        if (sites.length > 1) {
          this.router.navigate(['/site-master']);
          localStorage.setItem('isMultipleSites', 'true');
        } else if (sites.length === 1) {
          const find = this.auth.sidebarLinks.find(
            (c: any) => c.code === 'HOME'
          );
          if (!this.commonService.isNullOrUndefined(find)) {
            find.url = '/site';
            localStorage.setItem('siteId', sites[0].siteId);
            localStorage.setItem('siteName', sites[0].siteName);
            localStorage.setItem('isMultipleSites', 'false');
          }
          this.router.navigate(['/site'], {
            queryParams: { siteId: sites[0].siteId }
          });
        }
      }
    });
  }
}
